import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { handleApiBadges } from "@/lib/badges";
import { toast } from "sonner";
import { createChatRealtimeConnection } from "@/lib/chatRealtime";
import {
  enqueueOfflineMessage,
  listOfflineMessages,
  removeOfflineMessage,
  createClientMessageId,
} from "@/lib/chatOfflineQueue";
import { sortMessagesChronologically } from "@/lib/chatTimeline";

const FALLBACK_POLL_MS = 12000;
const VISIBLE_POLL_MS = 8000;

function mergeMessages(existing, incoming) {
  const map = new Map(existing.map((m) => [m.message_id, m]));
  for (const msg of incoming) {
    const prev = map.get(msg.message_id);
    map.set(msg.message_id, prev ? { ...prev, ...msg } : msg);
  }
  return sortMessagesChronologically([...map.values()]);
}

function upsertConversation(list, update) {
  const otherId = update.user_id || update.conversation_with;
  if (!otherId) return list;
  const idx = list.findIndex((c) => c.user_id === otherId);
  if (idx === -1) return list;
  const next = [...list];
  next[idx] = { ...next[idx], ...update };
  return next.sort((a, b) => {
    const timeA = a.last_message?.created_at ? new Date(a.last_message.created_at).getTime() : 0;
    const timeB = b.last_message?.created_at ? new Date(b.last_message.created_at).getTime() : 0;
    return timeB - timeA;
  });
}

export function useChatSync({ mode, selectedUserId, withUserId, onSelectUser }) {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [otherTyping, setOtherTyping] = useState(false);
  const [connectionState, setConnectionState] = useState("connecting");
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  const conversationsSinceRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const selectedUserIdRef = useRef(selectedUserId);
  const pollTimerRef = useRef(null);
  const realtimeRef = useRef(null);
  const preloadedRef = useRef(new Map());

  selectedUserIdRef.current = selectedUserId;

  const applyRealtimeEvents = useCallback((events) => {
    for (const envelope of events) {
      const { event, payload } = envelope;
      if (event === "message.new" && payload?.message) {
        const msg = payload.message;
        const partnerId = payload.conversation_with;
        if (selectedUserIdRef.current && partnerId === selectedUserIdRef.current) {
          setMessages((prev) => mergeMessages(prev, [msg]));
          lastMessageIdRef.current = msg.message_id;
        }
        setConversations((prev) => upsertConversation(prev, {
          user_id: partnerId,
          last_message: msg,
          unread_count: selectedUserIdRef.current === partnerId ? 0 : undefined,
        }));
      } else if (event === "conversation.updated" && payload?.message) {
        const partnerId = payload.conversation_with;
        setConversations((prev) => upsertConversation(prev, {
          user_id: partnerId,
          last_message: payload.message,
        }));
      } else if (event === "message.read" && payload?.reader_id) {
        setMessages((prev) => prev.map((m) => (
          m.receiver_id === payload.reader_id
            ? { ...m, is_read: true, delivery_status: "read" }
            : m
        )));
      } else if (event === "typing.start" && payload?.user_id === selectedUserIdRef.current) {
        setOtherTyping(true);
      } else if (event === "typing.stop" && payload?.user_id === selectedUserIdRef.current) {
        setOtherTyping(false);
      }
    }
  }, []);

  const sync = useCallback(async ({ forceConversations = false } = {}) => {
    const params = {};
    if (selectedUserIdRef.current) {
      params.conversation_with = selectedUserIdRef.current;
      if (lastMessageIdRef.current) {
        params.after_message_id = lastMessageIdRef.current;
      }
      params.typing_with = selectedUserIdRef.current;
    }
    if (forceConversations || !conversationsSinceRef.current) {
      params.conversations_since = conversationsSinceRef.current || undefined;
    } else {
      params.conversations_since = conversationsSinceRef.current;
    }

    try {
      const { data } = await api.get("/messages/sync", { params });
      conversationsSinceRef.current = data.server_time;

      if (Array.isArray(data.conversations)) {
        if (!conversationsSinceRef.current || forceConversations || data.conversations.length > 0) {
          setConversations((prev) => {
            if (!params.conversations_since || prev.length === 0) {
              return data.conversations;
            }
            const map = new Map(prev.map((c) => [c.user_id, c]));
            for (const c of data.conversations) {
              map.set(c.user_id, { ...map.get(c.user_id), ...c });
            }
            return [...map.values()].sort((a, b) => {
              const timeA = a.last_message?.created_at ? new Date(a.last_message.created_at).getTime() : 0;
              const timeB = b.last_message?.created_at ? new Date(b.last_message.created_at).getTime() : 0;
              return timeB - timeA;
            });
          });
        }
      }

      if (Array.isArray(data.messages) && data.messages.length > 0) {
        setMessages((prev) => {
          const merged = mergeMessages(prev, data.messages);
          lastMessageIdRef.current = merged[merged.length - 1]?.message_id || lastMessageIdRef.current;
          return merged;
        });
      }

      if (data.typing) {
        setOtherTyping(!!data.typing.active);
      }

      if (Array.isArray(data.events) && data.events.length > 0) {
        applyRealtimeEvents(data.events);
      }

      setConnectionState("synced");
      return data;
    } catch {
      setConnectionState("offline");
      return null;
    }
  }, [applyRealtimeEvents]);

  const loadInitialMessages = useCallback(async (userId) => {
    try {
      const { data } = await api.get(`/messages/${userId}`, { params: { limit: 50 } });
      const sorted = sortMessagesChronologically(data || []);
      setMessages(sorted);
      lastMessageIdRef.current = sorted[sorted.length - 1]?.message_id || null;
    } catch {
      setMessages([]);
      lastMessageIdRef.current = null;
    }
  }, []);

  const markConversationRead = useCallback(async (userId) => {
    try {
      await api.patch(`/messages/${userId}/read`);
      setConversations((prev) => prev.map((c) => (
        c.user_id === userId ? { ...c, unread_count: 0 } : c
      )));
      setMessages((prev) => prev.map((m) => (
        m.sender_id === userId ? { ...m, is_read: true, delivery_status: "read" } : m
      )));
    } catch {
      /* silencieux */
    }
  }, []);

  const loadOlderMessages = useCallback(async (userId) => {
    if (!userId || messages.length === 0) return false;
    const oldestId = messages[0]?.message_id;
    if (!oldestId || oldestId.startsWith("client_")) return false;

    try {
      const { data } = await api.get(`/messages/${userId}`, {
        params: { before: oldestId, limit: 40 },
      });
      if (!Array.isArray(data) || data.length === 0) return false;
      setMessages((prev) => mergeMessages(prev, data));
      return true;
    } catch {
      return false;
    }
  }, [messages]);

  const preloadConversation = useCallback(async (userId) => {
    if (preloadedRef.current.has(userId)) return;
    preloadedRef.current.set(userId, true);
    try {
      await api.get(`/messages/${userId}/preview`, { params: { limit: 20 } });
    } catch {
      preloadedRef.current.delete(userId);
    }
  }, []);

  const flushOfflineQueue = useCallback(async () => {
    const pending = await listOfflineMessages();
    for (const item of pending) {
      try {
        await api.post("/messages", item);
        await removeOfflineMessage(item.client_message_id);
      } catch {
        break;
      }
    }
    if (pending.length > 0) {
      await sync({ forceConversations: true });
    }
  }, [sync]);

  const sendMessage = useCallback(async ({
    receiverId,
    content,
    attachmentUrl,
    attachmentThumbUrl,
    attachmentType,
    optimisticMessage,
  }) => {
    const payload = {
      receiver_id: receiverId,
      content: content?.trim() || "",
      client_message_id: optimisticMessage.client_message_id,
      attachment_url: attachmentUrl,
      attachment_thumb_url: attachmentThumbUrl,
      attachment_type: attachmentType,
    };

    setMessages((prev) => mergeMessages(prev, [optimisticMessage]));

    if (!navigator.onLine) {
      await enqueueOfflineMessage(payload);
      return { queued: true, message: optimisticMessage };
    }

    try {
      const res = await api.post("/messages", payload);
      const data = res.data;
      handleApiBadges(res, toast);
      setMessages((prev) => mergeMessages(
        prev.filter((m) => m.client_message_id !== optimisticMessage.client_message_id),
        [data],
      ));
      lastMessageIdRef.current = data.message_id;
      setConversations((prev) => upsertConversation(prev, {
        user_id: receiverId,
        last_message: data,
      }));
      return { queued: false, message: data };
    } catch (err) {
      if (!err.response) {
        await enqueueOfflineMessage(payload);
        return { queued: true, message: optimisticMessage };
      }
      setMessages((prev) => prev.map((m) => (
        m.client_message_id === optimisticMessage.client_message_id
          ? { ...m, delivery_status: "failed" }
          : m
      )));
      throw err;
    }
  }, []);

  const uploadAttachment = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post("/messages/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  }, []);

  const signalTyping = useCallback((receiverId, active = true) => {
    api.post("/messages/typing", { receiver_id: receiverId, active }).catch(() => {});
  }, []);

  useEffect(() => {
    sync({ forceConversations: true });
  }, [sync]);

  useEffect(() => {
    if (!selectedUserId) {
      setMessages([]);
      setOtherTyping(false);
      lastMessageIdRef.current = null;
      return;
    }
    loadInitialMessages(selectedUserId);
    markConversationRead(selectedUserId);
  }, [selectedUserId, loadInitialMessages, markConversationRead]);

  useEffect(() => {
    if (!withUserId || conversations.length === 0) return;
    const match = conversations.find((c) => c.user_id === withUserId);
    if (match) onSelectUser?.(match);
  }, [withUserId, conversations, onSelectUser]);

  useEffect(() => {
    const schedulePoll = () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      const hidden = document.visibilityState === "hidden";
      const delay = hidden ? FALLBACK_POLL_MS : VISIBLE_POLL_MS;
      pollTimerRef.current = setTimeout(async () => {
        if (!hidden || !realtimeRef.current) {
          await sync();
        }
        schedulePoll();
      }, delay);
    };
    schedulePoll();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [sync]);

  useEffect(() => {
    realtimeRef.current = createChatRealtimeConnection({
      onEvents: applyRealtimeEvents,
      onReconnect: () => sync({ forceConversations: true }),
    });
    setConnectionState("connected");
    return () => realtimeRef.current?.close();
  }, [applyRealtimeEvents, sync]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      flushOfflineQueue();
      sync({ forceConversations: true });
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    flushOfflineQueue();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [flushOfflineQueue, sync]);

  return {
    conversations,
    messages,
    setMessages,
    otherTyping,
    connectionState,
    isOnline,
    sync,
    sendMessage,
    uploadAttachment,
    signalTyping,
    preloadConversation,
    loadOlderMessages,
    markConversationRead,
    createClientMessageId,
  };
}

export default useChatSync;